import React, {useMemo} from 'react';
import Popper from 'popper.js';
import {SPLIT_DELIMITER, splitAndRemoveEmpty} from "../../placement/const";
import CancelIcon from '@material-ui/icons/Cancel';

const noDisable = (_: string[], __: string) => false;

type Props = {
    options: string[];
    placeholder?: string;
    field: string;
    full?: boolean;
    width?: string;
    minWidth?: string;
    disabled?: boolean;
    value: string;
    setValue: (newVal: string) => void;
    haveSearchBar?: boolean;
    labelMapperFunc?: (value: string) => string;
    disableFunc?: (checked: string[], value: string) => boolean;
    hideFunc?: (value: string) => boolean;
    hasError?: boolean;
    showBadges?: boolean;
    onRemoveBadge?: (value: string) => void;
    /** Colour, shape and text size of a chip. The default is the blue pill every existing chip screen shows. */
    badgeClassName?: string;
}

const DEFAULT_BADGE_CLASS = 'bg-botticelli-100 text-white rounded-full text-xs';

export function DropdownMultiSelect({
    options,
    placeholder = 'Please Choose',
    full = false,
    width = 'w-64',
    minWidth,
    disabled = false,
    value,
    setValue,
    field,
    haveSearchBar = false,
    labelMapperFunc,
    disableFunc = noDisable,
    hideFunc,
    hasError = false,
    showBadges = false,
    onRemoveBadge,
    badgeClassName = DEFAULT_BADGE_CLASS,
}: Props) {
    const [dropdownPopoverShow, setDropdownPopoverShow] = React.useState(false);
    const btnDropdownRef = React.useRef(null);
    const popoverDropdownRef = React.useRef(null);
    const nodeRef = React.useRef(null);
    const borderColor = hasError ? 'red-500' : 'gray-400';
    let borderHoverColor = hasError ? 'red-500' : 'atlantis-100';
    if (disabled) {
        borderHoverColor = 'gray-400';
    }
    if (full) {
        width = 'w-full';
    }
    const haveRemoveBadgeButton = !!onRemoveBadge;

    // filter for checkbox
    const [filter, setFilter] = React.useState('');

    // init checkbox options
    const initSelect = options.reduce((m, option) => {
        m[option] = splitAndRemoveEmpty(value).includes(option);
        return m;
    }, {} as Record<string, boolean>);

    const [checkboxsSelected, setCheckboxsSelected] = React.useState<Record<string, boolean>>(initSelect);
    const setCheckboxSelectedWrapper = (select: string) => {
        checkboxsSelected[select] = !checkboxsSelected[select];
        setCheckboxsSelected({ ...checkboxsSelected });
    };
    const checkedOptions = useMemo(() => (Object.keys(checkboxsSelected).filter((k) => checkboxsSelected[k])), [checkboxsSelected]);

    React.useEffect(() => {
        setCheckboxsSelected(options.reduce((m, option) => {
            m[option] = splitAndRemoveEmpty(value).includes(option);
            return m;
        }, {} as Record<string, boolean>));
    }, [value, options])

    const clearAllSelected = () => {
        setCheckboxsSelected(options.reduce((m, option) => {
            m[option] = false;
            return m;
        }, {} as Record<string, boolean>))
    }
    const filteredOption = options.filter((option) => {
        return !hideFunc?.(option) && option.toLowerCase().includes(filter.toLowerCase());
    })


    const openDropdownPopover = () => {
        // for sonar cloud (this constructor use to set position of the dropdown)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _ = new Popper(btnDropdownRef.current!, popoverDropdownRef.current!, {
            placement: "bottom"
        });
        setDropdownPopoverShow(true);
    };

    const handleClickOutside = (e: Event) => {
        if ((nodeRef.current as any).contains(e.target)) {
            return;
        }
        setValue(checkedOptions.join(SPLIT_DELIMITER));
        setDropdownPopoverShow(false);
    };

    const valuesToLabels = (values: string) => {
        if (labelMapperFunc) {
            return splitAndRemoveEmpty(values).map((v) => labelMapperFunc(v)).join(SPLIT_DELIMITER);
        }
        return values;
    }

    React.useEffect(() => {
        // handle click outside
        if (dropdownPopoverShow) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdownPopoverShow, checkboxsSelected]);

    // handle value got reset will make  checkbox unselected all
    React.useEffect(() => {
        if (value === '') {
            setCheckboxsSelected(initSelect);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    return (
        <div ref={nodeRef} className={`inline-block relative text-sm ${width} ${minWidth || ''}`}
            data-testid={`multi-dd-${field}`}>
            <div ref={btnDropdownRef} data-testid={`multi-dd-opener-${field}`}>
                {showBadges && checkedOptions.length > 0 ? (
                    <div
                        className={`block w-full bg-white border border-${borderColor} hover:border-${borderHoverColor} ` +
                            `px-4 py-2 rounded shadow leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50 ` +
                            `min-h-[42px] flex flex-wrap items-center gap-2 pr-10 ` +
                            (disabled ? 'opacity-50' : 'cursor-pointer')}
                        onClick={disabled ? undefined : openDropdownPopover}
                    >
                        {checkedOptions.map((option) => (
                            <span
                                key={option}
                                className={`${badgeClassName} px-2 py-1 flex items-center`}
                            >
                                {labelMapperFunc ? labelMapperFunc(option) : option}
                                {haveRemoveBadgeButton && <button
                                    type="button"
                                    className="ml-1 bg-transparent outline-none focus:outline-none hover:opacity-70"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (onRemoveBadge) {
                                            onRemoveBadge(option);
                                        }
                                    }}
                                    disabled={disabled}
                                >
                                    <CancelIcon fontSize="small" />
                                </button>}
                            </span>
                        ))}
                    </div>
                ) : (
                    <input
                        name={field}
                        className={`block appearance-none w-full bg-white border border-${borderColor} hover:border-${borderHoverColor} ` +
                            `px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline disabled:opacity-50`}
                        onClick={openDropdownPopover}
                        value={valuesToLabels(value)}
                        disabled={disabled}
                        placeholder={placeholder}
                        style={{ caretColor: 'transparent' }}
                        readOnly
                    />
                )}
                <div className='pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700'>
                    <svg className="fill-current h-4 w-4" xmlns="https://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                </div>
            </div>
            <div ref={popoverDropdownRef}
                className={
                    (dropdownPopoverShow ? "block " : "hidden ") +
                    "p-5 bg-white text-black text-base z-50 float-left list-none text-left rounded shadow-lg mt-1 w-full"
                }
                data-testid={`multi-dd-list-${field}`}
            >
                {
                    haveSearchBar &&
                    <div className='mb-4'>
                        <input
                            className={`block appearance-none w-full bg-white border border-${borderColor} hover:border-${borderHoverColor}` +
                                ` px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline`}
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                    </div>
                }
                <div className='flex flex-col items-left scrollbar-always max-height-60'>
                    <div className='text-sm text-atlantis-200 pb-4 cursor-pointer hover:text-atlantis-300' onClick={() => clearAllSelected()}>
                        Clear All
                    </div>
                    {filteredOption.map((option) => {
                        const disabled = disableFunc(checkedOptions, option);
                        return (
                        <label key={option} className={`inline-flex items-center pb-3 pl-1 ${disabled ? 'cursor-not-allowed opacity-75' : ''}`}>
                            <input
                                type="checkbox"
                                disabled={disabled}
                                className={`form-checkbox h-4 w-4 text-botticelli-100 ${disabled ? 'cursor-not-allowed' : ''}`}
                                onChange={() => setCheckboxSelectedWrapper(option)}
                                checked={checkboxsSelected[option]} />
                            <span className="ml-2 text-sm text-gray-700">{labelMapperFunc ? labelMapperFunc(option) : option}</span>
                        </label>
                    )})}
                </div>
            </div>
        </div>);
}
